import { Form, FormItem, FormList } from '@magicdoorinc/form';
import { useLocalization, LabeledDateInputField, DateRange, cn, Button, Checkbox, LabeledTextInput, DropdownMenu } from '@magicdoorinc/ui';
import { createEffect, createMemo, createSignal, onMount, Show, untrack } from 'solid-js';
import PlusIcon from '~/assets/images/common/plus.svg?component-solid';
import IconRemove from '~/assets/images/common/remove.svg?component-solid';
import { LabelWithOptional } from '~/components/common';
import { DriverLicenseInput } from '~/components/common/DriverLicenseInput';
import { Section } from '~/components/common/FormSectionTitle';
import { LabeledGoogleAddressInput } from '~/components/common/Inputs/LabeledGoogleAddressInput';
import ExpandableSection from '~/components/common/expandables/ExpandableSection';
import { LanguageSelect } from '~/components/languages';
import { genderTypes } from '~/utils/constant';
import { tenantName } from '~/utils/formRules';
import type { BetterForm } from '@magicdoorinc/form';
import type { Component } from 'solid-js';

const EmergencyContacts = () => {
  const { t } = useLocalization();

  return (
    <FormList formFieldName={'emergencyContacts'}>
      <FormList.Items
        class="mb-4 flex flex-col gap-y-4"
        component={({ remove }) => {
          return (
            <div class="flex gap-x-5">
              <FormItem
                placeholder={t('Name')}
                label={t(`Name`)}
                class="grow"
                formFieldName={['name']}
                component={LabeledTextInput}
                rules={[{ message: t(`Please enter name`), required: true }]}
              />
              <FormItem
                class="grow"
                placeholder={t('Email')}
                label={t(`Email address`)}
                formFieldName={['email']}
                component={LabeledTextInput}
                rules={[{ type: 'email', message: t('Email is invalid') }]}
              />
              <FormItem
                class="grow"
                rules={[{ type: 'phone', message: t('Phone is invalid') }]}
                placeholder={t('Phone')}
                label={t(`Phone number`)}
                formFieldName={['phone']}
                component={LabeledTextInput}
              />
              <FormItem
                class="grow"
                placeholder={t('Relationship')}
                label={t(`Relationship`)}
                formFieldName={['relationship']}
                component={LabeledTextInput}
              />
              <div class="ml-2 mt-8 flex cursor-pointer items-start" onClick={remove}>
                <IconRemove class="size-5 text-primary" />
              </div>
            </div>
          );
        }}
      />

      <FormList.ActionArea>
        {({ add }) => {
          return (
            <Button color="white" onClick={() => add()}>
              <PlusIcon /> {t('Add more')}
            </Button>
          );
        }}
      </FormList.ActionArea>
    </FormList>
  );
};

export const AddOrEditTenantForm: Component<{
  form?: BetterForm.ContextValue;
  class?: string;
  isEdit?: boolean;
}> = (props) => {
  const { t } = useLocalization();

  const [isCompany, setIsCompany] = createSignal(false);

  const [expandableOpen, setExpandableOpen] = createSignal(true);

  const defaultLanguage = 'en';

  createEffect(() => {
    if (!props.form?.isValidate) {
      setExpandableOpen(true);
    }
  });

  const handleIsCompanyChange = (selected: boolean) => {
    setIsCompany(selected);
  };

  onMount(() => {
    if (props.form) {
      const hasLastName = !!props.form?.formStore.lastName;
      const hasFirstName = !!props.form?.formStore.firstName;
      props.form.setFieldValue('isCompany', !hasLastName && hasFirstName);

      if (!props.isEdit) {
        props.form.setFieldValue('sendPortalInvite', true);
      }
    }
    setIsCompany(!!props.form?.formStore.isCompany);
  });

  createEffect(() => {
    if (isCompany() && props.form) {
      props.form.resetFields(['lastName']);
    }
  });

  const emailRules = createMemo(() => {
    untrack(() => {
      props.form?.setFormError('email', undefined);
    });
    const rules: BetterForm.Rule[] = [{ type: 'email', message: t('Email is invalid') }];
    if (!props.isEdit && props.form?.formStore.sendPortalInvite) {
      rules.push({ required: true, message: t('{name} is required', { name: t('Email address') }) });
    }
    return rules;
  });

  return (
    <Form
      initialValues={{
        currentAddress: {},
        emergencyContacts: [],
        language: defaultLanguage,
      }}
      defaultForm={props.form}
      class={cn('text-left', props.class)}>
      <Section title={t('Tenant information')} border headerClass="mb-4 pb-2">
        <div class="grid grid-cols-2 gap-x-5 gap-y-4">
          <FormItem
            class="col-span-full md:col-span-1"
            options={[
              { label: t('Yes'), value: true },
              { label: t('No'), value: false },
            ]}
            label={t('Is company')}
            placeholder={t('Select')}
            onChangeMethodName="onChange"
            formFieldName="isCompany"
            component={DropdownMenu<boolean>}
            onChange={handleIsCompanyChange}
          />
          <div class="col-span-full md:col-span-1" />
          <Show
            when={!isCompany()}
            fallback={
              <FormItem
                class="col-span-full md:col-span-1"
                label={t('Company name')}
                placeholder={t('Please enter the {name}', { name: t('Company name') })}
                component={LabeledTextInput}
                formFieldName="firstName"
                rules={[
                  { required: true, message: t('{name} is required', { name: t('Company name') }) },
                  { length: 150, message: t('{name} must be {length} characters or less', { name: t('Company name'), length: '150' }) },
                ]}
              />
            }>
            <FormItem
              id="add-tenant-form-firstName"
              class="col-span-full lg:col-span-1"
              rules={[{ message: t(`Please enter first name`), required: true }, ...tenantName(t('First name'))]}
              formFieldName="firstName"
              label={t('First name')}
              component={LabeledTextInput}
              placeholder={t('Please enter')}
            />
            <FormItem
              id="add-tenant-form-lastName"
              class="col-span-full lg:col-span-1"
              rules={[...tenantName(t('Last name'))]}
              formFieldName="lastName"
              label={t('Last name')}
              component={LabeledTextInput}
              placeholder={t('Please enter')}
            />
          </Show>
        </div>
        <div class="grid grid-cols-2 items-end gap-4 gap-y-6 pt-4">
          <FormItem
            id="add-tenant-form-email"
            class="col-span-full lg:col-span-1"
            formFieldName="email"
            label={t('Email address')}
            component={LabeledTextInput}
            placeholder={t('Please enter')}
            rules={emailRules()}
          />
          <Show when={!props.isEdit}>
            <div class="h-10 leading-10">
              <FormItem
                component={Checkbox}
                id="add-tenant-form-send-portal-invitation"
                class="col-span-full lg:col-span-1"
                label={t('Send portal invitation')}
                valuePropName="checked"
                showLabel
                formFieldName="sendPortalInvite"
              />
            </div>
          </Show>
        </div>
      </Section>

      <ExpandableSection
        open={expandableOpen()}
        onOpenChange={setExpandableOpen}
        headerBorder
        class="rounded-lg border border-separator px-3 text-base"
        triggerTitle={<LabelWithOptional label={t('Advanced information')} />}>
        <div class="grid grid-cols-3 gap-x-5 gap-y-4 pb-3">
          <FormItem
            id="add-tenant-form-phone"
            class="col-span-full lg:col-span-1"
            formFieldName="phone"
            label={t('Phone number')}
            component={LabeledTextInput}
            placeholder={t('Please enter')}
            rules={[{ type: 'phone', message: t('Phone is invalid') }]}
          />
          <FormItem
            id="add-tenant-form-language"
            class="col-span-full lg:col-span-1"
            label={t('Language')}
            placeholder={t('Select language')}
            onChangeMethodName="onChange"
            formFieldName="language"
            component={LanguageSelect}
          />
        </div>
        <div class="grid grid-cols-3 gap-x-5 gap-y-4">
          <FormItem
            class="col-span-full lg:col-span-1"
            formFieldName="dateOfBirth"
            label={t('Date of birth')}
            component={LabeledDateInputField}
            dateRange={DateRange.Birthday}
            rules={[{ type: 'dateOfBirth', message: t('Date of birth is invalid. Age must be between 18 and 125 years.') }]}
          />
          <FormItem
            class="col-span-full lg:col-span-1"
            formFieldName="gender"
            label={t('Gender')}
            options={genderTypes}
            onChangeMethodName="onChange"
            component={DropdownMenu}
            placeholder={t('Select gender')}
          />
          <FormItem
            class="col-span-full lg:col-span-1"
            formFieldName="ssn"
            label={t('Social security number (SSN)')}
            component={LabeledTextInput}
            placeholder={t('Please enter')}
            rules={[{ type: 'ssn', message: t('Social security number is invalid') }]}
          />
          <div class="col-span-3 flex flex-col gap-x-5 gap-y-4">
            <FormItem
              class="col-span-3"
              formFieldName="currentAddress"
              label={t('Current address')}
              component={LabeledGoogleAddressInput}
            />
          </div>
          <div class="col-span-3 grid grid-cols-1 gap-x-5 gap-y-4 md:grid-cols-2">
            <DriverLicenseInput />
          </div>
        </div>

        <Section class="mt-4" title={t(`Emergency contact`)} border>
          <EmergencyContacts />
        </Section>
      </ExpandableSection>
    </Form>
  );
};
